import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Heart, X, Trash2, ExternalLink } from '@/lib/icons';
import { Link } from 'react-router-dom';
import { useFavorites, FavoriteProperty } from '@/hooks/useFavorites';
import { useToast } from '@/hooks/use-toast';
import { useBranding } from '@/contexts/BrandingContext';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface FavoritesModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const FavoritesModal: React.FC<FavoritesModalProps> = ({ open, onOpenChange }) => {
  const { favorites, removeFromFavorites, clearFavorites } = useFavorites();
  const { toast } = useToast();
  const { branding } = useBranding();
  const mainColor = branding?.primary_color || '#ef4444'; // default red-500

  const handleRemoveFavorite = (propertyId: string, propertyTitle: string) => {
    removeFromFavorites(propertyId);
  };

  const handleClearAll = () => {
    if (favorites.length === 0) return;
    clearFavorites();
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(price);
  };

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), "dd 'de' MMMM 'de' yyyy", { locale: ptBR });
  };

  const truncateTitle = (title: string, maxLength: number = 50) => {
    if (title.length <= maxLength) {
      return title;
    }
    return title.substring(0, maxLength).trim() + '...';
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] max-w-6xl h-[90vh] max-h-[800px] overflow-hidden flex flex-col" aria-describedby={undefined}>
        <DialogHeader className="flex-shrink-0 pb-4">
          <DialogTitle className="flex items-center gap-2">
            <Heart className="h-5 w-5 text-red-500 fill-current" />
            Meus Favoritos ({favorites.length})
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto min-h-0">
          {favorites.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Heart className="h-16 w-16 text-muted-foreground/50 mb-4" />
              <h3 className="text-lg font-semibold text-muted-foreground mb-2">
                Nenhum favorito ainda
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                Explore as propriedades e adicione as que mais gostar aos seus favoritos.
              </p>
              <Button onClick={() => onOpenChange(false)}>
                Explorar Propriedades
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
              {favorites.map((property) => (
                <div
                  key={property.id}
                  className="group relative bg-card border rounded-lg overflow-hidden hover:shadow-lg transition-all duration-300"
                >
                  {/* Imagem da propriedade */}
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img
                      src={property.image}
                      alt={property.title}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                    
                    {/* Badges */}
                    <div className="absolute top-3 left-3 flex gap-2">
                      <Badge 
                        variant={property.type === 'sale' ? 'default' : 'secondary'}
                        className="bg-background/90 text-foreground backdrop-blur-sm"
                      >
                        {property.type === 'sale' ? 'Venda' : 'Aluguel'}
                      </Badge>
                      {property.featured && (
                        <Badge className="bg-primary text-primary-foreground">
                          Destaque
                        </Badge>
                      )}
                    </div>

                    {/* Botão de remover dos favoritos */}
                    <Button
                      variant="ghost"
                      size="icon"
                      className="absolute top-3 right-3 bg-background/90 backdrop-blur-sm hover:bg-background text-red-500 hover:text-red-600"
                      onClick={() => handleRemoveFavorite(property.id, property.title)}
                    >
                      <Heart className="h-4 w-4 fill-current" />
                    </Button>
                  </div>

                  {/* Conteúdo do card */}
                  <div className="p-3">
                    <div className="space-y-2">
                      {/* Título */}
                      <h3 className="font-semibold text-xs group-hover:text-primary transition-colors" title={property.title}>
                        {truncateTitle(property.title)}
                      </h3>

                      {/* Endereço */}
                      <p className="text-xs text-muted-foreground line-clamp-1">
                        {property.address}
                      </p>

                      {/* Preço */}
                      <div className="flex items-center justify-between">
                        <span className="text-base font-bold text-gray-900">
                          {formatPrice(property.price)}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {property.type === 'sale' ? 'à vista' : '/mês'}
                        </span>
                      </div>

                      {/* Data de adição */}
                      <p className="text-xs text-muted-foreground">
                        Adicionado em {formatDate(property.addedAt)}
                      </p>

                      {/* Botão de ação */}
                      <div className="flex gap-2 pt-1">
                        <Button
                          asChild
                          size="sm"
                          className="flex-1 text-xs h-8"
                          style={{ 
                            backgroundColor: mainColor,
                            color: 'white'
                          }}
                        >
                          <Link to={`/imovel/${property.id}`}>
                            <ExternalLink className="h-3 w-3 mr-1" />
                            Ver Detalhes
                          </Link>
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Botão Limpar Todos - Apenas desktop, posicionado na parte inferior direita */}
        {favorites.length > 0 && (
          <div className="flex-shrink-0 pt-4 border-t border-border/50 hidden lg:block">
            <div className="flex justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={handleClearAll}
                className="text-destructive hover:text-destructive hover:bg-destructive/10"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Limpar Todos
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
